import { Wysiwyg } from "../wysiwyg/Wysiwyg";
import { Box, Unstable_Grid2 as Grid, Typography } from "@mui/material";
import { RecordContextType } from "../record/Record";
import { renderFieldView } from "../fieldsViews/renderFieldView";
import { findField } from "../../utils/findField";
import { TaskDoneShortcutWithNotifications } from "./TaskDoneShortcutWithNotifications";
import { Comments } from "../comments/Comments";
import { useTaskNotificationsRead } from "./useTaskNotificationsRead";
import { MarkNotificationsReadDialog } from "./MarkNotificationsReadDialog";
import { FormatedFields } from "../../types/fields.types";
import { Avatar } from "../fieldsViews/fields/Avatar";
import { getSessionStatusColor, useSessionStatusField } from "../../screens/sessions/components/neuro-assign-techs/useSessionStatus";
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator, timelineItemClasses } from "@mui/lab";

type TasksDetailProps = {
    context: RecordContextType;
};

export const TasksDetail = ({ context }: TasksDetailProps) => {
    const contentField = findField(context.formatedFields, "content");

    const { dialogProps, checkAndPrompt } = useTaskNotificationsRead(context.item.id);

    return (
        <Grid container spacing={2} mt={2} justifyContent="space-between" alignItems="flex-start">
            <Grid xs={12} lg={8}>
                <Wysiwyg entityForm={context.entityForm} initialValue={context.item.content} isDisabled={true} field={contentField} />
            </Grid>
            <Grid xs={12} lg={4} container spacing={5} pl={5}>
                <Grid xs={12} display="flex" alignItems="center" pl={1}>
                    <Typography variant="h6" color="primary">
                        Concluída:
                    </Typography>
                    <TaskDoneShortcutWithNotifications id={context.item.id} status={context.item.status} onStatusChange={checkAndPrompt} />
                </Grid>
                <TasksDetailViewFields task={context.item} taskFormatedFields={context.formatedFields} />
            </Grid>

            <Grid xs={12}>
                <Comments
                    comments={context.item.comments}
                    commentsEntity="task_comments"
                    relatedEntityField="task"
                    relatedEntity="tasks"
                    relatedEntityId={context.item.id}
                    wysiwygCanMention={true}
                    onInteraction={checkAndPrompt}
                />
            </Grid>

            <MarkNotificationsReadDialog {...dialogProps} />
        </Grid>
    );
};

type TasksDetailViewFieldsProps = {
    task: any;
    taskFormatedFields: FormatedFields;
    options?: { showAssociated?: boolean; isSideView?: boolean };
};

export const TasksDetailViewFields = ({
    task,
    taskFormatedFields,
    options: { showAssociated = true, isSideView = false } = {}
}: TasksDetailViewFieldsProps) => {
    const assignedUsers = findField(taskFormatedFields, "assigned_users");
    const participantUsers = findField(taskFormatedFields, "participant_users");
    const relatedWith = findField(taskFormatedFields, "related_with");
    const hasParticipants = Array.isArray(task?.participant_users) && task.participant_users.length > 0;
    const firstColumnFields = <TaskDatesFields task={task} taskFormatedFields={taskFormatedFields} />;

    const secondColumnFields = (
        <Grid xs={12} justifyContent="center">
            <Typography variant="subtitle1" color="primary">
                Atribuido:
            </Typography>
            {assignedUsers && renderFieldView(assignedUsers, task, "group")}
        </Grid>
    );

    const thirdColumnFields = (
        <>
            {hasParticipants && (
                <Grid xs={12} justifyContent="center">
                    <Typography variant="subtitle1" color="primary">
                        Participantes:
                    </Typography>
                    {participantUsers && renderFieldView(participantUsers, task, "group")}
                </Grid>
            )}
            {showAssociated && (
                <Grid xs={12}>
                    <Typography variant="subtitle1" color="primary">
                        Associado:
                    </Typography>
                    {relatedWith && renderFieldView(relatedWith, task, "group")}
                </Grid>
            )}
        </>
    );

    if (isSideView) {
        return (
            <Grid container spacing={3}>
                <Grid xs={12} md={4} container spacing={3}>
                    {firstColumnFields}
                </Grid>
                <Grid xs={12} md={4} container spacing={3}>
                    {secondColumnFields}
                </Grid>
                <Grid xs={12} md={4} container spacing={3}>
                    {thirdColumnFields}
                </Grid>
            </Grid>
        );
    }

    return (
        <>
            {firstColumnFields}
            {secondColumnFields}
            {thirdColumnFields}
        </>
    );
};

type TaskDatesFieldsProps = {
    task: any;
    taskFormatedFields: FormatedFields;
};

const TaskDatesFields = ({ task, taskFormatedFields }: TaskDatesFieldsProps) => {
    const dateCreatedField = findField(taskFormatedFields, "date_created");
    const dueDateField = findField(taskFormatedFields, "due_date");
    const doneDateField = findField(taskFormatedFields, "done_date");
    const isDone = Boolean(task?.done_by || task?.done_date);
    const sessionStatusField = useSessionStatusField();
    const createdColor = getSessionStatusColor(sessionStatusField, "booked");
    const deadlineColor = getSessionStatusColor(sessionStatusField, "awaiting_in_clinic");
    const doneColor = getSessionStatusColor(sessionStatusField, "done");

    return (
        <Box width="100%" pl={1} mb={3}>
            <Timeline sx={{ p: 0, m: 0, [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 } }}>
                <TaskTimelineStep title="Criada por:" dotColor={createdColor}>
                    <TaskUserAndDate user={task?.user_created} dateView={dateCreatedField && renderFieldView(dateCreatedField, task, "group")} />
                </TaskTimelineStep>
                <TaskTimelineStep title="Deadline:" dotColor={deadlineColor}>
                    {dueDateField && renderFieldView(dueDateField, task, "group")}
                </TaskTimelineStep>
                <TaskTimelineStep title="Concluída por:" dotColor={isDone ? doneColor : "grey.500"} isPending={!isDone} isLast>
                    {isDone && <TaskUserAndDate user={task?.done_by} dateView={doneDateField && renderFieldView(doneDateField, task, "group")} />}
                </TaskTimelineStep>
            </Timeline>
        </Box>
    );
};

type TaskTimelineStepProps = {
    title: string;
    dotColor: string;
    isPending?: boolean;
    isLast?: boolean;
    children: React.ReactNode;
};

const TaskTimelineStep = ({ title, dotColor, isPending = false, isLast = false, children }: TaskTimelineStepProps) => {
    return (
        <TimelineItem sx={{ minHeight: 0 }}>
            <TimelineSeparator>
                <Box
                    sx={{
                        height: (theme) => `calc(${theme.typography.subtitle1.fontSize} * ${theme.typography.subtitle1.lineHeight})`,
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <TimelineDot
                        sx={{
                            boxShadow: "none",
                            m: 0,
                            alignSelf: "center",
                            borderColor: dotColor,
                            backgroundColor: isPending ? "transparent" : dotColor
                        }}
                    />
                </Box>
                {!isLast && <TimelineConnector sx={{ backgroundColor: "grey.300", width: "2px" }} />}
            </TimelineSeparator>
            <TimelineContent sx={{ pt: 0, pb: "12px" }}>
                <Typography variant="subtitle1" color="primary">
                    {title}
                </Typography>
                {children}
            </TimelineContent>
        </TimelineItem>
    );
};

type TaskUser = {
    avatar?: string;
    first_name?: string;
    last_name?: string;
};

type TaskUserAndDateProps = {
    user?: TaskUser;
    dateView: React.ReactNode;
};

const TaskUserAndDate = ({ user, dateView }: TaskUserAndDateProps) => {
    return (
        <Box display="flex" alignItems="center" gap={2}>
            {user && <Avatar value={user.avatar} styles={{ width: 40, height: 40 }} />}
            <Box sx={{ "& .MuiTypography-root": { lineHeight: 1.25 } }}>
                {user && (
                    <Typography variant="body1">
                        {user.first_name} {user.last_name}
                    </Typography>
                )}
                {dateView}
            </Box>
        </Box>
    );
};
